import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from 'capacitor-native-biometric';

@Injectable({ providedIn: 'root' })
export class BiometricService {
  private readonly SERVER = 'network.rsm';

  // Token guardado no login para oferecer a ativação depois da fase inicial (onboarding).
  private pendingOfferToken: string | null = null;

  constructor(private alertController: AlertController) {}

  isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** Agenda a oferta de ativação da biometria, sem exibir nada ainda. */
  queueEnableOffer(refreshToken: string): void {
    this.pendingOfferToken = refreshToken ?? null;
  }

  /**
   * Exibe a oferta de ativação se houver uma agendada. Consome uma única vez
   * (chamado ao chegar na home, após o onboarding).
   */
  async consumeEnableOffer(): Promise<void> {
    const refreshToken = this.pendingOfferToken;
    this.pendingOfferToken = null;

    if (!refreshToken) return;

    const available = await this.isAvailable();
    const enabled = await this.isEnabled();
    if (!available || enabled) return;

    const alert = await this.alertController.create({
      header: 'Ativar Biometria',
      message: 'Deseja usar digital ou Face ID para entrar mais rápido?',
      buttons: [
        { text: 'Não', role: 'cancel' },
        { text: 'Sim', handler: () => { this.enableFromOffer(refreshToken); } },
      ],
    });
    await alert.present();
  }

  /** Salva a credencial e dá feedback ao usuário sobre o resultado da ativação. */
  private async enableFromOffer(refreshToken: string): Promise<void> {
    try {
      await this.save(refreshToken);
      await this.presentInfo(
        'Biometria ativada',
        'Pronto! No próximo login você poderá entrar com a digital ou Face ID.'
      );
    } catch {
      await this.presentInfo(
        'Não foi possível ativar',
        'Houve um problema ao ativar a biometria. Tente novamente mais tarde.'
      );
    }
  }

  private async presentInfo(header: string, message: string): Promise<void> {
    const alert = await this.alertController.create({ header, message, buttons: ['OK'] });
    await alert.present();
  }

  async isAvailable(): Promise<boolean> {
    if (!this.isNative()) return false;
    try {
      const result = await NativeBiometric.isAvailable();
      return result.isAvailable;
    } catch {
      return false;
    }
  }

  async isEnabled(): Promise<boolean> {
    if (!this.isNative()) return false;
    try {
      const cred = await NativeBiometric.getCredentials({ server: this.SERVER });
      return !!cred?.password;
    } catch {
      return false;
    }
  }

  async save(refreshToken: string): Promise<void> {
    await NativeBiometric.setCredentials({
      username: 'rsm_user',
      password: refreshToken,
      server: this.SERVER,
    });
  }

  /**
   * Mantém o refreshToken guardado na biometria sincronizado com o token mais
   * recente da sessão — mas SOMENTE se a biometria já estiver habilitada.
   * Sem isso, a rotação de refresh token durante a sessão deixa o token salvo
   * velho/inválido e o login por biometria falha (401) depois do logout.
   */
  async syncStoredToken(refreshToken?: string): Promise<void> {
    if (!this.isNative() || !refreshToken) return;
    try {
      const cred = await NativeBiometric.getCredentials({ server: this.SERVER });
      if (cred?.password && cred.password !== refreshToken) {
        await this.save(refreshToken);
      }
    } catch {
      // biometria não habilitada/indisponível — nada a sincronizar
    }
  }

  async getRefreshToken(): Promise<string | null> {
    try {
      const cred = await NativeBiometric.getCredentials({ server: this.SERVER });
      return cred?.password ?? null;
    } catch {
      return null;
    }
  }

  async delete(): Promise<void> {
    try {
      await NativeBiometric.deleteCredentials({ server: this.SERVER });
    } catch {}
  }

  // true enquanto o diálogo nativo de biometria está aberto (e por um curto
  // período depois). Usado pelo app-lock para ignorar o background/foreground
  // causado pelo próprio diálogo e evitar o loop de re-bloqueio.
  private _verifying = false;
  get verifying(): boolean {
    return this._verifying;
  }

  async verify(): Promise<boolean> {
    this._verifying = true;
    try {
      await NativeBiometric.verifyIdentity({
        reason: 'Acesse sua conta RSM',
        title: 'Biometria RSM',
        subtitle: 'Use sua digital ou Face ID',
        description: 'Confirme sua identidade para entrar',
      });
      return true;
    } catch {
      return false;
    } finally {
      // Mantém a flag por um instante para cobrir o evento de foreground
      // que chega logo após o diálogo fechar.
      setTimeout(() => { this._verifying = false; }, 1500);
    }
  }
}
